import { FC, useMemo, useState } from 'react';

import {
  AdditionalOption,
  GiftItemInfoType,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import {
  GiftOrderDeliveryMode,
  GiftSelectedOption,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { BucketImg } from '@shared/components/BucketImg';
import { formatPrice } from '@shared/utils/formatPrice';

import { GiftAdditionalOptions } from './GiftAdditionalOptions';
import { buildSelectedOptions, selectedOptionsToMap } from '../utils/giftOption.util';

export interface GiftCheckoutItemCardProps {
  itemName: string;
  itemImage?: string;
  priceRetail: number;
  quantity: number;
  minOrderQuantity: number;
  orderUnit: number;
  onIncrease: () => void;
  onDecrease: () => void;
  itemType?: GiftItemInfoType;
  deliveryMode?: GiftOrderDeliveryMode;
  selectedOptions?: GiftSelectedOption[];
  /** 추가옵션 정의 (제공 + onChangeOptions 있을 때 결제화면에서 옵션 변경 가능) */
  additionalOptions?: AdditionalOption<Image>[];
  onChangeOptions?: (next: GiftSelectedOption[]) => void;
  /** 소량주문 개당 추가금 (옵션처럼 단가에 가산). 0이면 미표시 */
  unitSurcharge?: number;
  /** 추가금 면제 최소수량 (단가 옆 "N개미만 +금액" 주석용) */
  surchargeMinQty?: number;
}

function DeliveryBadge({
  itemType,
  deliveryMode,
}: {
  itemType?: GiftItemInfoType;
  deliveryMode?: GiftOrderDeliveryMode;
}) {
  if (!itemType || itemType === GiftItemInfoType.Coupon) return null;

  const label =
    deliveryMode === GiftOrderDeliveryMode.Individual
      ? '수령자 입력 후 2~3일 내 배송'
      : '배송기간: 2~3일 소요';

  return (
    <span className="mt-1 inline-block rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-600">
      {label}
    </span>
  );
}

export const GiftCheckoutItemCard: FC<GiftCheckoutItemCardProps> =
  function GiftCheckoutItemCard({
    itemName,
    itemImage,
    priceRetail,
    quantity,
    minOrderQuantity,
    orderUnit,
    onIncrease,
    onDecrease,
    itemType,
    deliveryMode,
    selectedOptions,
    additionalOptions,
    onChangeOptions,
    unitSurcharge = 0,
    surchargeMinQty,
  }) {
    const optionDelta = (selectedOptions ?? []).reduce(
      (s, o) => s + (o.priceDelta || 0),
      0,
    );
    const unitPrice = priceRetail + optionDelta + unitSurcharge;

    // 옵션 변경 가능 여부 + 인라인 에디터 상태
    const editableOptions = useMemo(
      () => (additionalOptions ?? []).filter((o) => (o.choices ?? []).length > 0),
      [additionalOptions],
    );
    const canEditOptions = !!onChangeOptions && editableOptions.length > 0;
    const [editingOptions, setEditingOptions] = useState(false);
    const selectedMap = useMemo(
      () => selectedOptionsToMap(selectedOptions),
      [selectedOptions],
    );

    const handleSelectOption = (
      optionName: string,
      slotIndex: number,
      choiceName: string,
    ) => {
      if (!onChangeOptions) return;
      const arr = [...(selectedMap[optionName] ?? [])];
      arr[slotIndex] = choiceName;
      const nextMap = { ...selectedMap, [optionName]: arr };
      onChangeOptions(buildSelectedOptions(editableOptions, nextMap));
    };
    return (
      <div className="border-b pb-3 last:border-b-0">
        <div className="flex gap-3">
          <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded bg-gray-100">
            {itemImage ? (
              <BucketImg
                value={itemImage}
                size="w200"
                alt={itemName}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                No Image
              </div>
            )}
          </div>

          <div className="flex-1">
            <p className="text-sm font-medium">{itemName}</p>
            {/* 선택한 옵션 칩 + 변경 버튼 */}
            {selectedOptions?.length || canEditOptions ? (
              <div className="mt-1 flex flex-wrap items-center gap-1">
                {selectedOptions?.map((o, i) => (
                  <span
                    key={`${o.optionName}#${o.slotIndex ?? 0}:${o.choiceName}:${i}`}
                    className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600"
                  >
                    {o.optionName}
                    {o.slotIndex != null ? o.slotIndex + 1 : ''}
                    <span className="mx-0.5 text-gray-300">·</span>
                    <span className="text-gray-800">{o.choiceName}</span>
                    {o.priceDelta > 0 && (
                      <span className="ml-0.5 text-indigo-500">
                        +{formatPrice(o.priceDelta)}
                      </span>
                    )}
                  </span>
                ))}
                {canEditOptions && (
                  <button
                    type="button"
                    onClick={() => setEditingOptions((v) => !v)}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold transition ${
                      editingOptions
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-600'
                        : 'border-gray-300 bg-white text-gray-500 hover:border-indigo-400 hover:text-indigo-600'
                    }`}
                  >
                    {editingOptions ? '✕ 닫기' : '✎ 옵션 변경'}
                  </button>
                )}
              </div>
            ) : null}

            <p className="mt-0.5 text-sm font-bold">
              {formatPrice(unitPrice)}원
              {unitSurcharge > 0 && (
                <span className="ml-1 text-xs font-normal text-amber-600">
                  ({surchargeMinQty != null ? `${surchargeMinQty}개 미만 ` : ''}+
                  {formatPrice(unitSurcharge)}원)
                </span>
              )}
            </p>
            <DeliveryBadge itemType={itemType} deliveryMode={deliveryMode} />

            <div className="mt-2 flex items-center gap-1">
              <button
                type="button"
                onClick={onDecrease}
                disabled={quantity <= minOrderQuantity}
                className="flex h-7 w-7 items-center justify-center rounded border bg-gray-50 text-sm font-bold disabled:text-gray-300"
              >
                −
              </button>
              <span className="min-w-[32px] text-center text-sm font-bold">
                {quantity}
              </span>
              <button
                type="button"
                onClick={onIncrease}
                className="flex h-7 w-7 items-center justify-center rounded border bg-gray-50 text-sm font-bold"
              >
                +
              </button>
              {orderUnit > 1 && (
                <span className="ml-1 text-[10px] text-gray-400">
                  ({orderUnit}개 단위)
                </span>
              )}
            </div>
          </div>

          <div className="text-right">
            <p className="text-sm font-bold text-red-500">
              {formatPrice(unitPrice * quantity)}원
            </p>
          </div>
        </div>

        {/* 옵션 변경 에디터 — 카드 전체 너비 사용 (라벨은 위 칩에 있으므로 셀렉터만) */}
        {canEditOptions && editingOptions && (
          <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50/80 p-3">
            <GiftAdditionalOptions
              options={editableOptions}
              selected={selectedMap}
              onSelect={handleSelectOption}
              hideLabels
              className="space-y-2"
            />
          </div>
        )}
      </div>
    );
  };
